import { useEffect, useMemo, useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import CurrencyInput from '../inputs/CurrencyInput';
import useTips, { TipOption } from '../../hooks/order/useTips';
import { Collection } from 'collect.js';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useTheme from '../../hooks/ui/useTheme';
import PencilIcon from '../../assets/logo/PencilIcon';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { useTranslation } from 'react-i18next';

interface Props {
  className?: string;
}

interface Item extends TipOption {
  is_selected: boolean;
  amount: number;
}

export default function Tips(props: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Tips' });

  const { className } = props;

  const theme = useTheme();
  const { formatCurrency } = useAppMethods();
  const { options, calculate } = useTips();
  const { data, localData, update } = useOrderFlow();

  const [activeIndex, setActiveIndex] = useState(getDefaultActiveIndex());
  const [customTipAmount, setCustomTipAmount] = useState<number>(localData.tip);

  const customTipInputRef = useRef(null);

  const items = useMemo<Collection<Item>>(
    () =>
      options.map((item) => ({
        ...item,
        is_selected: activeIndex === item.index,
        amount: item.is_custom ? customTipAmount : calculate(data.sub_total, item),
      })),
    [options, activeIndex, data.sub_total, customTipAmount],
  );

  const predefinedItems = useMemo<Item[]>(
    () => items.filter((item: Item) => !item.is_zero && !item.is_custom).toArray(),
    [items],
  );

  const zeroOption = items.first();
  const customOption = items.firstWhere('is_custom');
  const selectedOption = items.firstWhere('is_selected');

  useEffect(() => {
    const option = localData.tip === undefined ? items.firstWhere('is_default', true) : selectedOption;

    option?.percentage && onSelect(option);
  }, [data.sub_total]);

  function getDefaultActiveIndex() {
    if (localData.tip === 0) {
      return options.firstWhere('is_zero', true).index;
    }

    return options.first(
      (item) => calculate(data.sub_total, item) === localData.tip,
      options.firstWhere('is_custom', true),
    ).index;
  }

  const onChangeCustomTip = (value: number) => {
    setCustomTipAmount(value);
    update('tip', value);
  };

  const onSelect = (option: Item) => {
    setActiveIndex(option.index);
    update('tip', option?.is_custom ? customTipAmount : calculate(data.sub_total, option));

    // if (option.is_custom) {
    //   setTimeout(() => customTipInputRef.current.focus(), 5);
    // }
  };

  return (
    <div className={className}>
      <Accordion expanded={customOption.is_selected} style={{ boxShadow: 'none', backgroundColor: 'transparent' }}>
        <AccordionSummary
          style={{
            padding: 0,
            margin: 0,
            width: '100%',
            minHeight: 0,
          }}
          classes={{
            content: '!m-none',
          }}
        >
          <div className="w-full grid grid-cols-4 gap-xs flex-nowrap">
            {predefinedItems.map((item: Item) => {
              return (
                <label htmlFor={`tip-option-${item.index}`} className={itemContainer} key={`tip-option-${item.index}`}>
                  <div className="flex justify-between items-center">
                    {/* {item.is_selected && (
                      <CheckCircleIcon
                        color={theme.color.brand.primary}
                        className="h-[12px] w-[12px] -mt-[1px] ml-2xs"
                      />
                    )} */}
                  </div>
                  <p className="text-center text-xs text-brand-text-secondary">{formatCurrency(item.amount)}</p>
                  <h6 className="my-auto text-center text-sm">{item.percentage}%</h6>
                  {item.description && <p className="text-xs">{item.description}</p>}
                  <input
                    type="radio"
                    checked={item.is_selected}
                    onChange={(e) => e.target.checked && onSelect(item)}
                    className="hidden"
                    id={`tip-option-${item.index}`}
                  />
                </label>
              );
            })}
            <label htmlFor={`tip-option-${customOption.index}`} className={itemContainer}>
              <div className="w-full h-full flex justify-center items-center">
                <PencilIcon color={theme.color.brand.text.primary} />
                <input
                  type="radio"
                  checked={customOption.is_selected}
                  onChange={(e) => e.target.checked && onSelect(customOption)}
                  id={`tip-option-${customOption.index}`}
                  className="hidden"
                />
              </div>
            </label>
          </div>
        </AccordionSummary>
        <AccordionDetails style={{ padding: 0, marginTop: 0 }}>
          <div className="mt-xs">
            <CurrencyInput
              id="custom-tip-input"
              value={customOption.amount}
              onChange={onChangeCustomTip}
              ref={customTipInputRef}
            />
          </div>
          {customOption.amount > 500 && (
            <p className="mt-xs text-danger">{t('form.custom_tip.validation.max', { value: formatCurrency(500) })}</p>
          )}
        </AccordionDetails>
      </Accordion>
      <label htmlFor={`tip-option-${zeroOption.index}`} className={noTipButton}>
        <p className="text-sm text-brand-text-secondary">{zeroOption.description}</p>
        <input
          type="radio"
          checked={zeroOption.is_selected}
          onChange={(e) => e.target.checked && onSelect(zeroOption)}
          id={`tip-option-${zeroOption.index}`}
          className="hidden"
        />
      </label>
    </div>
  );
}

const itemContainer =
  'py-xs space-y-[2px] px-sm flex flex-col justify-center items-center rounded-lg cursor-pointer transition-colors ease-in-out duration-200 [&:not(:checked)]:border has-[:checked]:border-brand-primary';
const noTipButton =
  'mt-xs py-xs w-full rounded-lg flex justify-center items-center cursor-pointer transition-colors ease-in-out duration-200 [&:not(:checked)]:border has-[:checked]:border-brand-primary';
